import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LoadingButton from '@mui/lab/LoadingButton';
import { Card, CardActions, CardContent, Grid, TextField, Typography } from '@mui/material';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { ChangeEvent, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAxios from '../../../hooks/useAxios';
import { SettingsContext } from '../../../store/SettingsContext';
import { handleHttpError } from '../../../utils/common';
import StyledCardTitleBar from '../../common/StyledCardTitleBar';

interface CreateForm {
    username: string;
    cognitoUsername: string;
    email: string;
    password: string;
    contactEmail: string;
    telegramChatId: string;
    realName: string;
    userAgent: string;
    cookie: string;
    xbc: string;
}

const EMPTY_FORM: CreateForm = {
    username: '',
    cognitoUsername: '',
    email: '',
    password: '',
    contactEmail: '',
    telegramChatId: '',
    realName: '',
    userAgent: '',
    cookie: '',
    xbc: '',
};

const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

/**
 * Creates a brand new subscriber with the minimum required details.
 * All other settings are edited on the subscriber's edit page after creation.
 */
const AdminSubscriberCreateCard = () => {
    const settingsContext = useContext(SettingsContext);
    const axios = useAxios();
    const dialog = useDialog();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [form, setForm] = useState<CreateForm>(EMPTY_FORM);
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);

    const setField = (key: keyof CreateForm) => (e: ChangeEvent<HTMLInputElement>) =>
        setForm(prev => ({ ...prev, [key]: e.target.value }));

    const requiredError = (value: string): boolean => submitted && value.trim() === '';

    const emailError = (value: string, required: boolean): string | null => {
        if (!submitted) {
            return null;
        }

        if (required && value.trim() === '') {
            return 'This field is required';
        }

        if (value.trim() !== '' && !isValidEmail(value.trim())) {
            return 'Enter a valid email address';
        }

        return null;
    };

    const isFormValid = (): boolean =>
        form.username.trim() !== '' &&
        isValidEmail(form.email.trim()) &&
        form.password !== '' &&
        form.telegramChatId.trim() !== '' &&
        (form.contactEmail.trim() === '' || isValidEmail(form.contactEmail.trim())) &&
        form.userAgent.trim() !== '' &&
        form.cookie.trim() !== '' &&
        form.xbc.trim() !== '';

    const handleCreate = async () => {
        setSubmitted(true);

        if (!isFormValid() || saving) {
            return;
        }

        setSaving(true);

        await axios
            .post(settingsContext.routes.subscribers.base, {
                username: form.username.trim(),
                ...(form.cognitoUsername.trim() !== '' && { cognito: { username: form.cognitoUsername.trim() } }),
                email: form.email.trim(),
                password: form.password,
                ...(form.contactEmail.trim() !== '' && { contactEmail: form.contactEmail.trim() }),
                telegramChatId: form.telegramChatId.trim(),
                ...(form.realName.trim() !== '' && { realName: form.realName.trim() }),
                header: {
                    'User-Agent': form.userAgent.trim(),
                    Cookie: form.cookie.trim(),
                    'x-bc': form.xbc.trim(),
                },
            })
            .then(response => {
                enqueueSnackbar('Subscriber created', { variant: 'success' });

                navigate(`/subscribers/${response.data._id}/edit`);
            })
            .catch(error => {
                console.error('createSubscriber', error);
                handleHttpError(error, dialog);
            })
            .finally(() => {
                setSaving(false);
            });
    };

    return (
        <Card variant="elevation">
            <StyledCardTitleBar title="New Subscriber" />
            <CardContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2">Account</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            required
                            label="OnlyFans Username"
                            value={form.username}
                            onChange={setField('username')}
                            error={requiredError(form.username)}
                            helperText={requiredError(form.username) ? 'This field is required' : undefined}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Cognito Username"
                            value={form.cognitoUsername}
                            onChange={setField('cognitoUsername')}
                            helperText="Dashboard login this subscriber belongs to"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            required
                            label="OnlyFans Email"
                            value={form.email}
                            onChange={setField('email')}
                            error={emailError(form.email, true) !== null}
                            helperText={emailError(form.email, true) || undefined}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            required
                            type="password"
                            label="OnlyFans Password"
                            value={form.password}
                            onChange={setField('password')}
                            error={submitted && form.password === ''}
                            helperText={submitted && form.password === '' ? 'This field is required' : undefined}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2">Contact</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Contact Email"
                            value={form.contactEmail}
                            onChange={setField('contactEmail')}
                            error={emailError(form.contactEmail, false) !== null}
                            helperText={emailError(form.contactEmail, false) || undefined}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            required
                            label="Telegram Chat ID"
                            value={form.telegramChatId}
                            onChange={setField('telegramChatId')}
                            error={requiredError(form.telegramChatId)}
                            helperText={requiredError(form.telegramChatId) ? 'This field is required' : undefined}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Real Name" value={form.realName} onChange={setField('realName')} />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2">OnlyFans Session Header</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            required
                            label="User-Agent"
                            value={form.userAgent}
                            onChange={setField('userAgent')}
                            error={requiredError(form.userAgent)}
                            helperText={requiredError(form.userAgent) ? 'This field is required' : undefined}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            required
                            multiline
                            minRows={2}
                            label="Cookie"
                            value={form.cookie}
                            onChange={setField('cookie')}
                            error={requiredError(form.cookie)}
                            helperText={requiredError(form.cookie) ? 'This field is required' : undefined}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            required
                            label="x-bc"
                            value={form.xbc}
                            onChange={setField('xbc')}
                            error={requiredError(form.xbc)}
                            helperText={requiredError(form.xbc) ? 'This field is required' : undefined}
                        />
                    </Grid>
                </Grid>
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end', paddingX: 2, paddingBottom: 2 }}>
                <LoadingButton variant="contained" startIcon={<PersonAddIcon />} loading={saving} onClick={handleCreate}>
                    Create Subscriber
                </LoadingButton>
            </CardActions>
        </Card>
    );
};

export default AdminSubscriberCreateCard;
